import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import RaceRecommender from '../RaceFinder/RaceRecommender';
import './OnboardingPage.css';

type Step = 'syncing' | 'goals' | 'race_location' | 'race_results';

const OnboardingPage: React.FC = () => {
  const { user, setNeedsOnboarding } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('syncing');
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState('Connecting to Strava...');
  const [syncResult, setSyncResult] = useState<any>(null);
  const syncStarted = useRef(false);

  const [zipCode, setZipCode] = useState('');
  const [userLat, setUserLat] = useState<number | undefined>();
  const [userLng, setUserLng] = useState<number | undefined>();
  const [locationLabel, setLocationLabel] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [completing, setCompleting] = useState(false);

  // Strava sync on mount with simulated progress
  useEffect(() => {
    if (syncStarted.current) return;
    syncStarted.current = true;

    // Animate progress bar while sync runs
    setSyncProgress(5);
    setSyncStatus('Connecting to Strava...');

    const progressSteps = [
      { delay: 500, progress: 15, status: 'Fetching your activities...' },
      { delay: 2000, progress: 35, status: 'Importing runs...' },
      { delay: 4000, progress: 55, status: 'Analyzing your workouts...' },
      { delay: 6000, progress: 70, status: 'Almost there...' },
    ];

    const timers: NodeJS.Timeout[] = [];
    progressSteps.forEach(({ delay, progress, status }) => {
      timers.push(setTimeout(() => {
        setSyncProgress(prev => Math.max(prev, progress));
        setSyncStatus(status);
      }, delay));
    });

    // Actual sync call
    api.syncStravaActivities(90)
      .then((result) => {
        setSyncResult(result);
        setSyncProgress(100);
        setSyncStatus('Sync complete!');
        setTimeout(() => setStep('goals'), 800);
      })
      .catch(() => {
        // Sync failed — still proceed to goals
        setSyncProgress(100);
        setSyncStatus('Sync complete!');
        setTimeout(() => setStep('goals'), 800);
      });

    return () => timers.forEach(clearTimeout);
  }, []);

  const completeOnboarding = async (path: string) => {
    setCompleting(true);
    try {
      const location = userLat != null && userLng != null
        ? { latitude: userLat, longitude: userLng, label: locationLabel }
        : undefined;
      await api.completeOnboarding(path, location);
      setNeedsOnboarding(false);
      navigate('/dashboard', { replace: true });
    } catch {
      setCompleting(false);
    }
  };

  const handleDistancePlan = (distance: string) => {
    const location = userLat != null && userLng != null
      ? { latitude: userLat, longitude: userLng, label: locationLabel }
      : undefined;
    setCompleting(true);
    api.completeOnboarding('race', location).then(() => {
      setNeedsOnboarding(false);
      const params = new URLSearchParams();
      params.set('distance_label', distance);
      navigate(`/race-goal?${params.toString()}`);
    }).catch(() => setCompleting(false));
  };

  const handleTrainForRace = () => {
    setStep('race_location');
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser');
      return;
    }
    setGeoLoading(true);
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        setLocationLabel('Current location');
        setGeoLoading(false);
        setStep('race_results');
      },
      () => {
        setGeoError('Location access denied. You can enter a ZIP code instead.');
        setGeoLoading(false);
      },
      { timeout: 10000 }
    );
  };

  const handleZipSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!zipCode.trim()) return;
    setLocationLabel(zipCode.trim());
    setStep('race_results');
  };

  const handleSkipLocation = () => {
    setStep('race_results');
  };

  const handleSelectRace = (race: any) => {
    const location = userLat != null && userLng != null
      ? { latitude: userLat, longitude: userLng, label: locationLabel }
      : undefined;

    api.completeOnboarding('race', location).then(() => {
      setNeedsOnboarding(false);
      const params = new URLSearchParams();
      params.set('race_name', race.name);
      if (race.race_date) params.set('race_date', race.race_date);
      if (race.distances?.length > 0) params.set('distance_label', race.distances[0]);
      if (race.city) params.set('city', race.city);
      if (race.state) params.set('state', race.state);
      navigate(`/race-goal?${params.toString()}`);
    });
  };

  const handleDifferentRace = () => {
    const location = userLat != null && userLng != null
      ? { latitude: userLat, longitude: userLng, label: locationLabel }
      : undefined;
    api.completeOnboarding('race', location).then(() => {
      setNeedsOnboarding(false);
      navigate('/race-goal');
    });
  };

  return (
    <div className="onboarding">
      {/* Decorative gradient blurs */}
      <div className="onboarding__glow onboarding__glow--1" aria-hidden="true" />
      <div className="onboarding__glow onboarding__glow--2" aria-hidden="true" />
      <div className="onboarding__glow onboarding__glow--3" aria-hidden="true" />
      <div className="onboarding__container">
        <div className="onboarding__header">
          <h1>Welcome{user?.first_name ? `, ${user.first_name}` : ''}!</h1>
          <p className="onboarding__subtitle">
            {step === 'syncing'
              ? 'We\'re importing your Strava activities...'
              : 'Let\'s get you set up with your training.'}
          </p>
        </div>

        {/* Step 0: Strava sync with progress bar */}
        {step === 'syncing' && (
          <div className="onboarding__step onboarding__sync">
            <div className="onboarding__sync-icon">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/c/cb/Strava_Logo.svg"
                alt="Strava"
                className="onboarding__strava-logo"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
            <div className="onboarding__progress-bar">
              <div
                className="onboarding__progress-fill"
                style={{ width: `${syncProgress}%` }}
              />
            </div>
            <p className="onboarding__sync-status">{syncStatus}</p>
            {syncResult && syncResult.synced > 0 && (
              <p className="onboarding__sync-count">
                Found {syncResult.synced} activities
              </p>
            )}
          </div>
        )}

        {/* Step 1: Choose your goal */}
        {step === 'goals' && (
          <div className="onboarding__step">
            <h2>What's your goal?</h2>

            <div className="onboarding__paths">
              <div className="onboarding__path-card onboarding__path-card--race">
                <div className="onboarding__card-left">
                  <div className="onboarding__path-header">
                    <h3>Train for a Race</h3>
                    <p>Get a personalized training plan for an upcoming race</p>
                  </div>
                  <div className="onboarding__race-options">
                    <button
                      className="onboarding__path-btn onboarding__path-btn--primary"
                      onClick={handleDifferentRace}
                      disabled={completing}
                    >
                      Enter Manually
                    </button>
                    <button
                      className="onboarding__path-btn onboarding__path-btn--secondary"
                      onClick={handleTrainForRace}
                    >
                      Find Races Near Me
                    </button>
                  </div>
                </div>
                <div className="onboarding__card-icon onboarding__card-icon--race">
                  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 8h4v32h-4z" fill="#32a2e6"/>
                    <path d="M18 8c6-2 10 2 16 0v16c-6 2-10-2-16 0z" fill="url(#flag-grad)"/>
                    <path d="M30 38l6-6m0 0l-4-2m4 2l-2 4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
                    <defs><linearGradient id="flag-grad" x1="18" y1="8" x2="34" y2="24"><stop stopColor="#32a2e6"/><stop offset="1" stopColor="#1e8fd0" stopOpacity="0.7"/></linearGradient></defs>
                  </svg>
                </div>
              </div>

              <div className="onboarding__path-card">
                <div className="onboarding__card-left">
                  <div className="onboarding__path-header">
                    <h3>Run a 5K or 10K</h3>
                    <p>A progressive plan to get you running consistently</p>
                  </div>
                  <div className="onboarding__race-options">
                    <button
                      className="onboarding__path-btn onboarding__path-btn--primary"
                      onClick={() => handleDistancePlan('5K')}
                      disabled={completing}
                    >
                      5K Plan
                    </button>
                    <button
                      className="onboarding__path-btn onboarding__path-btn--primary"
                      onClick={() => handleDistancePlan('10K')}
                      disabled={completing}
                    >
                      10K Plan
                    </button>
                  </div>
                </div>
                <div className="onboarding__card-icon onboarding__card-icon--distance">
                  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 36c4-4 8-12 14-12s6 8 10 8 6-4 12-8" stroke="#32a2e6" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
                    <circle cx="16" cy="14" r="5" fill="#32a2e6" opacity="0.9"/>
                    <path d="M16 19v8l-4 9" stroke="#32a2e6" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M16 19v8l4 9" stroke="#32a2e6" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 24h8" stroke="#32a2e6" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>

              <div className="onboarding__path-card">
                <div className="onboarding__card-left">
                  <div className="onboarding__path-header">
                    <h3>Just Track My Fitness</h3>
                    <p>Dashboard, AI coach, and weekly summaries — no training plan</p>
                  </div>
                  <button
                    className="onboarding__path-btn onboarding__path-btn--secondary"
                    onClick={() => completeOnboarding('fitness_only')}
                    disabled={completing}
                  >
                    Go to Dashboard
                  </button>
                </div>
                <div className="onboarding__card-icon onboarding__card-icon--fitness">
                  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="4" y="28" width="6" height="12" rx="2" fill="#32a2e6" opacity="0.5"/>
                    <rect x="13" y="22" width="6" height="18" rx="2" fill="#32a2e6" opacity="0.65"/>
                    <rect x="22" y="16" width="6" height="24" rx="2" fill="#32a2e6" opacity="0.8"/>
                    <rect x="31" y="10" width="6" height="30" rx="2" fill="#32a2e6"/>
                    <path d="M6 20l8-6 9 4 8-8 9-2" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="42" cy="8" r="3" fill="#22c55e"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Location (only after choosing "Train for a Race") */}
        {step === 'race_location' && (
          <div className="onboarding__step">
            <h2>Where do you run?</h2>
            <p className="onboarding__hint">This helps us find races near you.</p>

            <div className="onboarding__location-options">
              <button
                className="onboarding__geo-btn"
                onClick={handleUseLocation}
                disabled={geoLoading}
              >
                {geoLoading ? 'Detecting...' : 'Use My Location'}
              </button>

              <div className="onboarding__divider">
                <span>or</span>
              </div>

              <form className="onboarding__zip-form" onSubmit={handleZipSubmit}>
                <input
                  type="text"
                  className="onboarding__zip-input"
                  placeholder="Enter ZIP code"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  maxLength={10}
                />
                <button type="submit" className="onboarding__zip-btn" disabled={!zipCode.trim()}>
                  Go
                </button>
              </form>

              {geoError && <p className="onboarding__geo-error">{geoError}</p>}
            </div>

            <button className="onboarding__skip" onClick={handleSkipLocation}>
              Skip — I'll pick my own race
            </button>
          </div>
        )}

        {/* Step 3: Race recommendations */}
        {step === 'race_results' && (
          <div className="onboarding__step">
            <h2>Races near you</h2>
            <div className="onboarding__recommender-area">
              <RaceRecommender
                lat={userLat}
                lng={userLng}
                inline
                onSelectRace={handleSelectRace}
              />
            </div>
            <div className="onboarding__race-actions">
              <button className="onboarding__alt-link" onClick={handleDifferentRace}>
                I have a different race in mind
              </button>
              <button className="onboarding__back-link" onClick={() => setStep('goals')}>
                &larr; Back to goals
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
